import type { DocumentLink, PlanDocument, Project } from "@/engine/types";
import { documentIsUnlinked, type LinkCatalog } from "./doc-links";
import {
  documentCode,
  documentLibraryScale,
  documentMatchesQuery,
  documentTitle,
  type DocLinkFilter,
  type DocSection,
  type DocSortKey,
} from "./doc-model";

export type WorkspaceDocProject = Pick<Project, "id" | "name" | "documents" | "documentLinks" | "executionTasks" | "tasks" | "records">;

export type WorkspaceDocRow = {
  key: string;
  projectId: string;
  projectName: string;
  doc: PlanDocument;
  links: DocumentLink[];
  catalog: LinkCatalog;
};

export type WorkspaceDocScope = "all" | "plan" | "private";

export type WorkspaceDocQuery = {
  text: string;
  section: DocSection;
  scope: WorkspaceDocScope;
  mine: boolean;
  link: DocLinkFilter;
  planId: string;
  actorId: string;
  /** 본문을 읽을 수 없으면 번호·제목·플랜 이름만 맞춘다. */
  contentReadable: (row: WorkspaceDocRow) => boolean;
};

/** 읽을 수 있는 살아있는 문서. 휴지통은 플랜 목록에만 둔다. */
export function collectWorkspaceDocuments(
  projects: readonly WorkspaceDocProject[],
  readable: (doc: PlanDocument, projectId: string) => boolean,
): WorkspaceDocRow[] {
  const out: WorkspaceDocRow[] = [];
  for (const project of projects) {
    const projectName = project.name?.trim() || "이름 없는 플랜";
    const catalog = linkCatalog(project);
    const links = project.documentLinks ?? [];
    for (const doc of project.documents ?? []) {
      if (doc.deletedAt || !readable(doc, project.id)) continue;
      out.push({
        key: `${project.id}:${doc.id}`,
        projectId: project.id,
        projectName,
        doc,
        links: links.filter((link) => link.documentId === doc.id),
        catalog,
      });
    }
  }
  return out;
}

function linkCatalog(project: WorkspaceDocProject): LinkCatalog {
  return {
    executionTasks: project.executionTasks ?? [],
    tasks: (project.tasks ?? []).map((task) => ({ id: task.id, name: task.name, percentComplete: task.percentComplete })),
    records: project.records ?? [],
  };
}

/** 워크스페이스 문서 화면의 규모. 탭·필터를 바꿔도 같은 목록으로 적는다. */
export function workspaceDocumentScale(rows: readonly Pick<WorkspaceDocRow, "projectId" | "doc">[]): string {
  const plans = new Set(rows.map((row) => row.projectId));
  return documentLibraryScale(
    rows.map((row) => row.doc),
    { plans: plans.size },
  );
}

export function workspaceDocMatches(row: WorkspaceDocRow, query: string, readableContent: boolean): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (row.projectName.toLowerCase().includes(q)) return true;
  if (!readableContent) return `${documentCode(row.doc.seq)} ${row.doc.title}`.toLowerCase().includes(q);
  return documentMatchesQuery(row.doc, q);
}

export function filterWorkspaceDocuments<T extends WorkspaceDocRow>(rows: readonly T[], query: WorkspaceDocQuery): T[] {
  return rows.filter((row) => {
    if (query.planId && row.projectId !== query.planId) return false;
    if (query.scope !== "all" && row.doc.visibility !== query.scope) return false;
    if (query.mine && row.doc.authorId !== query.actorId) return false;
    if (query.link === "none" && !documentIsUnlinked(row.links, row.doc.id)) return false;
    if (query.section !== "all" && row.doc.kind !== query.section) return false;
    return workspaceDocMatches(row, query.text, query.contentReadable(row));
  });
}

function compareDocs(a: PlanDocument, b: PlanDocument, key: DocSortKey): number {
  if (key === "title") return documentTitle(a.title).localeCompare(documentTitle(b.title), "ko") || b.seq - a.seq;
  if (key === "seq") return a.seq - b.seq;
  return a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : b.seq - a.seq;
}

export function sortWorkspaceDocuments<T extends WorkspaceDocRow>(rows: readonly T[], key: DocSortKey): T[] {
  const next = [...rows];
  next.sort((a, b) => {
    const byDoc = compareDocs(a.doc, b.doc, key);
    if (byDoc !== 0) return byDoc;
    return a.projectName.localeCompare(b.projectName, "ko") || a.projectId.localeCompare(b.projectId);
  });
  return next;
}

/** 명령 팔레트. 최근 수정 8개, 검색이면 플랜 이름도 본다. */
export function paletteWorkspaceDocuments(rows: readonly WorkspaceDocRow[], query: string): WorkspaceDocRow[] {
  const q = query.trim().toLowerCase();
  const matched = q ? rows.filter((row) => row.projectName.toLowerCase().includes(q) || documentMatchesQuery(row.doc, q)) : sortWorkspaceDocuments(rows, "updated");
  return matched.slice(0, 8);
}
